import { ElMessage, type MessageHandler } from "element-plus";
import type { MessageType } from "element-plus/es/components/message/src/message";
import { transformI18n } from "@/plugins/i18n";

/**
 * 消息提示配置接口
 */
interface MessageOptions {
  /** 是否为国际化key */
  isI18nKey?: boolean;
  /** 国际化key或直接消息内容 */
  content: string;
  /** 消息类型 */
  type?: MessageType;
  /** 显示时长(毫秒),设为0则不自动关闭 */
  duration?: number;
  /** 是否显示关闭按钮 */
  showClose?: boolean;
  /** 是否将消息居中显示 */
  center?: boolean;
  /** 文字是否居中 */
  dangerouslyUseHTMLString?: boolean;
  /** 自定义类名 */
  customClass?: string;
  /** 关闭时的回调函数 */
  onClose?: () => void;
  /** 消息距离窗口顶部的偏移量 */
  offset?: number;
  /** 是否将消息实例添加到分组中 */
  grouping?: boolean;
}

/**
 * 消息提示工厂类
 */
class MessageFactory {
  /** 默认配置 */
  private defaultOptions: Partial<MessageOptions> = {
    duration: 3000,
    showClose: false,
    center: false,
    offset: 20,
    grouping: false
  };

  /**
   * 核心消息显示方法
   */
  private show(options: MessageOptions): MessageHandler {
    const {
      isI18nKey = false,
      content,
      type = "info",
      ...restOptions
    } = options;

    // 处理消息内容
    const message = isI18nKey ? transformI18n(content) : content;

    // 合并配置
    const finalOptions = {
      ...this.defaultOptions,
      ...restOptions,
      message,
      type
    };

    return ElMessage(finalOptions);
  }

  /**
   * 成功提示
   * @param content 国际化key或消息内容
   * @param isI18nKey 是否为国际化key,默认false
   * @param options 其他配置项
   */
  success(
    content: string,
    isI18nKey = false,
    options?: Omit<MessageOptions, "content" | "isI18nKey" | "type">
  ): MessageHandler {
    return this.show({
      content,
      isI18nKey,
      type: "success",
      ...options
    });
  }

  /**
   * 警告提示
   * @param content 国际化key或消息内容
   * @param isI18nKey 是否为国际化key,默认false
   * @param options 其他配置项
   */
  warning(
    content: string,
    isI18nKey = false,
    options?: Omit<MessageOptions, "content" | "isI18nKey" | "type">
  ): MessageHandler {
    return this.show({
      content,
      isI18nKey,
      type: "warning",
      ...options
    });
  }

  /**
   * 信息提示
   * @param content 国际化key或消息内容
   * @param isI18nKey 是否为国际化key,默认false
   * @param options 其他配置项
   */
  info(
    content: string,
    isI18nKey = false,
    options?: Omit<MessageOptions, "content" | "isI18nKey" | "type">
  ): MessageHandler {
    return this.show({
      content,
      isI18nKey,
      type: "info",
      ...options
    });
  }

  /**
   * 错误提示
   * @param content 国际化key或消息内容
   * @param isI18nKey 是否为国际化key,默认false
   * @param options 其他配置项
   */
  error(
    content: string,
    isI18nKey = false,
    options?: Omit<MessageOptions, "content" | "isI18nKey" | "type">
  ): MessageHandler {
    return this.show({
      content,
      isI18nKey,
      type: "error",
      ...options
    });
  }

  /**
   * 自定义消息类型
   * @param options 完整配置项
   */
  custom(options: MessageOptions): MessageHandler {
    return this.show(options);
  }

  /**
   * 关闭所有消息
   */
  closeAll(): void {
    ElMessage.closeAll();
  }

  /**
   * 更新默认配置
   * @param options 要更新的配置项
   */
  setDefaultOptions(options: Partial<MessageOptions>): void {
    this.defaultOptions = {
      ...this.defaultOptions,
      ...options
    };
  }
}

// 导出单例
/**
 * 消息提示工厂单例
 */
export const message = new MessageFactory();

// 默认导出
export default message;
